import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { adminApi } from "../../lib/authApi";
import { KpiGrid, SectionTitle, EmptyNote, MetricTable, pct, hrs, num, inr } from "./AnalyticsShared";

const RANGES = [["30d", "30 days"], ["90d", "90 days"], ["365d", "12 months"], ["all", "All time"]];
const FUNNEL = ["DRAFT", "SUBMITTED", "QUOTED", "APPROVED", "REJECTED"];
const PIPELINE = ["ORDER_CREATED", "CONFIRMED", "PARTNER_ASSIGNED", "ACCEPTED", "IN_PRODUCTION", "QUALITY_PENDING", "INSPECTION_IN_PROGRESS", "QUALITY_PASSED", "QUALITY_FAILED", "REWORK_REQUIRED", "READY_FOR_DISPATCH", "DISPATCHED", "IN_TRANSIT", "DELIVERED", "DELIVERY_CONFIRMED", "ON_HOLD", "CANCELLED"];

const Chart = ({ children, testId }) => (
  <div className="h-64 p-4 rounded-xl bg-card border border-border" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
  </div>
);

export const AdminAnalytics = () => {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    adminApi.get(`/admin/analytics?range=${range}`).then((r) => setData(r.data)).catch(() => setError("Could not load analytics."));
  }, [range]);

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Deterministic metrics derived from RFQ, order, quality and delivery records. Counts, rates and medians only — no predictions.</p>
        <div className="flex gap-1 rounded-full border border-border p-1" data-testid="analytics-range-selector">
          {RANGES.map(([k, label]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`analytics-range-${k}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${range === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{label}</button>
          ))}
        </div>
      </div>
      {error && <p className="mt-6 text-sm text-destructive" data-testid="analytics-error">{error}</p>}
      {!data && !error && <div className="py-16 flex justify-center" data-testid="analytics-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.5} /></div>}
      {data && data.empty && <div className="mt-6"><EmptyNote testId="analytics-empty" /></div>}
      {data && !data.empty && (
        <div className="mt-6">
          <KpiGrid items={[
            { label: "RFQs", value: num(data.rfq.total), hint: `${num(data.rfq.quoted)} quoted · ${num(data.rfq.approved)} approved`, testId: "kpi-rfqs" },
            { label: "Quoted → Approved", value: pct(data.rfq.rates.quoted_to_approved), hint: `${inr(data.rfq.approved_value)} of ${inr(data.rfq.quoted_value)}`, testId: "kpi-conversion" },
            { label: "Orders", value: num(data.orders.total), hint: `${num(data.orders.active)} active · ${num(data.orders.delivery_confirmed)} confirmed`, testId: "kpi-orders" },
            { label: "First-pass yield", value: pct(data.quality.first_pass_yield), hint: `${data.quality.first_cycle_passed}/${data.quality.first_cycle_inspections} cycle-0 inspections`, testId: "kpi-fpy" },
            { label: "Rework rate", value: pct(data.quality.rework_rate), hint: `${data.quality.rework_cycles_total} rework cycle(s)`, testId: "kpi-rework" },
            { label: "On-time delivery", value: pct(data.delivery.on_time_rate), hint: `${data.delivery.on_time}/${data.delivery.delivered} delivered vs quoted lead time`, testId: "kpi-ontime" },
            { label: "Submit → quote", value: hrs(data.rfq.submit_to_quote), hint: `median · n=${data.rfq.submit_to_quote.sample}`, testId: "kpi-quote-time" },
            { label: "S4 drawing RFQs", value: pct(data.rfq.source_share.s4_rate), hint: `${data.rfq.source_share.S4} S4 · ${data.rfq.source_share.S2} S2`, testId: "kpi-s4-share" },
          ]} />

          <SectionTitle>RFQ funnel</SectionTitle>
          <Chart testId="chart-rfq-funnel">
            <BarChart data={FUNNEL.map((s) => ({ status: s, count: data.rfq.by_status[s] || 0 }))}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="status" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </Chart>

          <SectionTitle>Order pipeline</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="order-pipeline">
            {PIPELINE.map((s) => (
              <div key={s} className="px-3 py-2 rounded-lg border border-border bg-card text-xs flex justify-between gap-2" data-testid={`pipeline-${s.toLowerCase()}`}>
                <span className="text-muted-foreground truncate">{s.replace(/_/g, " ")}</span><b className="font-mono-tech">{data.orders.by_status[s] || 0}</b>
              </div>
            ))}
          </div>

          <SectionTitle>Production & delivery durations (median)</SectionTitle>
          <KpiGrid cols="sm:grid-cols-2 lg:grid-cols-5" items={[
            { label: "Confirm → assign", value: hrs(data.production.confirm_to_assign), hint: `n=${data.production.confirm_to_assign.sample}`, testId: "kpi-confirm-assign" },
            { label: "Assign → accept", value: hrs(data.production.assign_to_accept), hint: `n=${data.production.assign_to_accept.sample}`, testId: "kpi-assign-accept" },
            { label: "Accept → production complete", value: hrs(data.production.accept_to_production_complete), hint: `n=${data.production.accept_to_production_complete.sample}`, testId: "kpi-production" },
            { label: "Transit", value: hrs(data.delivery.transit), hint: `n=${data.delivery.transit.sample}`, testId: "kpi-transit" },
            { label: "Delivery confirmation lag", value: hrs(data.delivery.confirmation_lag), hint: `n=${data.delivery.confirmation_lag.sample}`, testId: "kpi-confirm-lag" },
          ]} />

          <SectionTitle>Top failed checklist items</SectionTitle>
          <MetricTable testId="failed-checklist-table" rowKey="key" rows={data.quality.top_failed_checklist_keys} columns={[{ key: "key", label: "Checklist key" }, { key: "count", label: "Failures" }]} />

          <SectionTitle>Partner operational metrics</SectionTitle>
          <MetricTable testId="partner-table" rowKey="partner_id" rows={data.partners} columns={[
            { key: "company_name", label: "Partner" }, { key: "assignments", label: "Assigned" }, { key: "accepted", label: "Accepted" }, { key: "declined", label: "Declined" },
            { key: "response_time", label: "Response (median)", render: (r) => hrs(r.response_time) }, { key: "production", label: "Production (median)", render: (r) => hrs(r.production) },
            { key: "fpy", label: "FPY", render: (r) => pct(r.quality.first_pass_yield) }, { key: "rework", label: "Rework", render: (r) => pct(r.quality.rework_rate) },
            { key: "ontime", label: "On-time", render: (r) => pct(r.delivery.on_time_rate) }, { key: "active_orders", label: "Active" },
          ]} />

          <SectionTitle>Process performance</SectionTitle>
          <MetricTable testId="process-table" rowKey="process" rows={data.processes} columns={[
            { key: "process", label: "Process" }, { key: "rfq_count", label: "RFQs" }, { key: "order_count", label: "Orders" },
            { key: "fpy", label: "FPY", render: (r) => pct(r.first_pass_yield) }, { key: "rework", label: "Rework", render: (r) => pct(r.rework_rate) },
            { key: "prod", label: "Production (median)", render: (r) => hrs(r.accept_to_production_complete) }, { key: "ontime", label: "On-time", render: (r) => pct(r.on_time_rate) },
          ]} />

          <SectionTitle>Monthly trends</SectionTitle>
          <div className="grid lg:grid-cols-2 gap-4">
            <Chart testId="chart-trend-counts">
              <LineChart data={data.trends}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="rfqs" stroke="hsl(var(--primary))" strokeWidth={2} dot /><Line type="monotone" dataKey="orders" stroke="#0d9488" strokeWidth={2} dot /></LineChart>
            </Chart>
            <Chart testId="chart-trend-value">
              <BarChart data={data.trends}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => inr(v)} /><Legend />
                <Bar dataKey="quoted_value" name="Quoted ₹" fill="#94a3b8" radius={[4, 4, 0, 0]} /><Bar dataKey="approved_value" name="Approved ₹" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} /></BarChart>
            </Chart>
          </div>
        </div>
      )}
    </div>
  );
};
